/**
 * Pure TEA update for sticky per-section outline ribbon destinations.
 *
 * Shell: `useEditorNavStore.ts` (Pinia).
 */
import { assertNever } from "../lib/assertNever";
import { isNothing } from "../lib/maybe";
import {
  DEFAULT_SECTION_ROUTES,
  sectionFromPath,
  type EditorSection,
} from "../router/projectPaths";

export type NavSection = keyof typeof DEFAULT_SECTION_ROUTES;

export type EditorNavModel = {
  readonly lastRouteBySection: Readonly<Record<NavSection, string>>;
};

export const initialEditorNavModel: EditorNavModel = {
  lastRouteBySection: { ...DEFAULT_SECTION_ROUTES },
};

export type EditorNavMsg =
  | { readonly type: "RememberPath"; readonly fullPath: string }
  | {
      readonly type: "RememberSectionRoute";
      readonly section: EditorSection;
      readonly path: string;
    };

/** No effects: every Msg only changes the model. */
export type EditorNavCmd = never;

function withRoute(
  model: EditorNavModel,
  section: NavSection,
  path: string,
): EditorNavModel {
  return {
    lastRouteBySection: { ...model.lastRouteBySection, [section]: path },
  };
}

export function updateEditorNav(
  model: EditorNavModel,
  msg: EditorNavMsg,
): readonly [EditorNavModel, readonly EditorNavCmd[]] {
  switch (msg.type) {
    case "RememberPath": {
      const section = sectionFromPath(msg.fullPath);
      if (isNothing(section)) return [model, []];
      if (section.value in model.lastRouteBySection === false) {
        return [model, []];
      }
      const bare = msg.fullPath.split("?")[0] ?? "/";
      return [withRoute(model, section.value as NavSection, bare), []];
    }
    case "RememberSectionRoute": {
      if (msg.section in model.lastRouteBySection === false) {
        return [model, []];
      }
      const bare = msg.path.split("?")[0] ?? msg.path;
      return [withRoute(model, msg.section as NavSection, bare), []];
    }
    default:
      return assertNever(msg);
  }
}
